import React, { useState, useEffect } from "react";
const EyeIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export default function ReactivateAttuidModal({
  open,
  titleId = "reactivate-attuid-title",
  lookupPayload,
  workerName,
  workerDtvId,
  workerAttuid,
  secLabel,
  confirmLabel,
  lookupSecExist,
  securityEditEnabled,
  showSecurityEditButton,
  showSecurityDisableEditButton,
  onEnableSecurityEdit,
  onDisableSecurityEdit,
  securityInputValue,
  confirmInputValue,
  securityMaskedValue,
  confirmMaskedValue,
  showSecurity,
  showConfirmSecurity,
  onToggleShowSecurity,
  onToggleShowConfirmSecurity,
  securityLocked,
  isUS,
  submitting,
  onSecurityChange,
  onConfirmSecurityChange,
  errors,
  associationStatus,
  associationMessage,
  missingCriticalFields,
  onCancel,
  onValidate,
  onSubmit,
  validating,
  validateDisabled,
  validateStatus,
  validateMessage,
  submitDisabled,
  submitMessage,
  validateButtonStyle,
  loading,
}) {
  const [validateClicked, setValidateClicked] = useState(false);
  useEffect(() => {
    if (!open || !validating) {
      setValidateClicked(false);
    }
  }, [open, validating]);
  if (!open) return null;
  // console.log("lookupPayload:", lookupPayload);
  const normalizeSecExist = (value) => {
    const normalized = String(value ?? "")
      .trim()
      .replace(/^['\"]+|['\"]+$/g, "")
      .toLowerCase();
    if (["yes", "y", "true", "1", "t"].includes(normalized)) return "yes";
    if (["no", "n", "false", "0", "f"].includes(normalized)) return "no";
    return "";
  };

  const resolveSecExistFromPayload = (src) => {
    if (!src || typeof src !== "object") return "";

    const direct =
      src?.isSecExist ??
      src?.isSecExists ??
      src?.isSecExitst ??
      src?.isSecExits ??
      src?.is_sec_exist ??
      src?.is_sec_exists ??
      src?.isSecurityExist ??
      src?.isSecurityExists ??
      src?.securityExists ??
      src?.security_exist ??
      src?.securityIdExists ??
      src?.response?.isSecExist ??
      src?.response?.isSecExists ??
      src?.response?.isSecExitst ??
      src?.response?.isSecExits ??
      src?.response?.is_sec_exist ??
      src?.response?.is_sec_exists ??
      src?.response?.isSecurityExist ??
      src?.response?.isSecurityExists ??
      src?.response?.securityExists ??
      src?.response?.security_exist ??
      src?.response?.securityIdExists;

    const normalizedDirect = normalizeSecExist(direct);
    if (normalizedDirect) return normalizedDirect;

    const keySet = new Set([
      "issecexist",
      "issecexists",
      "issecexitst",
      "issecexits",
      "issecurityexist",
      "issecurityexists",
      "securityexists",
      "securityexist",
      "securityidexists",
    ]);

    const scan = (obj) => {
      if (!obj || typeof obj !== "object") return "";
      for (const key of Object.keys(obj)) {
        const normalizedKey = String(key || "")
          .toLowerCase()
          .replace(/[^a-z0-9]/g, "");
        if (!keySet.has(normalizedKey)) continue;
        const normalizedVal = normalizeSecExist(obj[key]);
        if (normalizedVal) return normalizedVal;
      }
      return "";
    };

    return (
      scan(src) ||
      scan(src?.response) ||
      scan(src?.form1) ||
      scan(src?.data) ||
      ""
    );
  };

  const normalizedLookupSecExist =
    resolveSecExistFromPayload(lookupPayload) ||
    normalizeSecExist(lookupSecExist);

  const isSecExistYes = normalizedLookupSecExist === "yes";
  const effectiveShowSecurityEditButton = Boolean(
    isSecExistYes && !securityEditEnabled,
  );
  const effectiveShowSecurityDisableEditButton = Boolean(
    isSecExistYes && securityEditEnabled,
  );
  const lockedMaskValue = isUS ? "*********" : "********";
  const hideSecurityValue = securityLocked || !showSecurity;
  const hideConfirmSecurityValue = securityLocked || !showConfirmSecurity;
  const securityMaskDisplay = securityLocked
    ? lockedMaskValue
    : securityMaskedValue;
  const confirmMaskDisplay = securityLocked
    ? lockedMaskValue
    : confirmMaskedValue;
  const securityRenderValue = securityLocked
    ? lockedMaskValue
    : securityInputValue;
  const confirmRenderValue = securityLocked
    ? lockedMaskValue
    : confirmInputValue;
  const disableModalButtons = submitting || loading;
  const checkingAssociation = associationStatus === "checking";
  const submitButtonBusy = submitting || loading || checkingAssociation;
  const validateButtonBusy = validating || loading;
  const invalidRoleMessage =
    "Invalid role, Please use the edit to change the role and resubmit.";
  const hasSecurityValidationErrors = Boolean(
    errors?.securityId || errors?.confirmSecurityId,
  );
  const validateMessageText = String(validateMessage || "").trim();
  const isRoleValidationMessage =
    validateMessageText === invalidRoleMessage ||
    /invalid role|role catalog|primary selling location|selected when one is not required/i.test(
      validateMessageText,
    );
  const isInvalidRoleValidation = Boolean(
    validateStatus === "failed" &&
    isRoleValidationMessage &&
    !hasSecurityValidationErrors,
  );
  const showInvalidRoleDetail =
    isInvalidRoleValidation && validateMessageText !== invalidRoleMessage;
  const validateButtonDisabled = Boolean(
    validateClicked ||
    validateDisabled ||
    disableModalButtons ||
    checkingAssociation ||
    validateButtonBusy ||
    isInvalidRoleValidation,
  );
  const submitButtonLabel = submitting
    ? "Submitting..."
    : loading
      ? "Loading..."
      : checkingAssociation
        ? "Checking..."
        : "Submit";
  const validateButtonLabel =
    validateClicked || validating
      ? "Validating..."
      : loading
        ? "Loading..."
        : "Validate";

  const validateTone =
    validateStatus === "passed"
      ? "#166534"
      : validateStatus === "failed"
        ? "#b91c1c"
        : validateStatus === "checking"
          ? "#1d4ed8"
          : "#6b7280";

  const resolvedValidateButtonStyle = {
    cursor: validateButtonDisabled ? "not-allowed" : "pointer",
    border: "1px solid",
    borderRadius: 10,
    padding: "10px 16px",
    fontWeight: 700,
    color: "#ffffff",
    background: validateButtonDisabled
      ? "linear-gradient(135deg, #94a3b8 0%, #cbd5e1 100%)"
      : "linear-gradient(135deg, #94a3b8 0%, #1d4ed8 100%)",
    borderColor: validateButtonDisabled ? "#94a3b8" : "#1d4ed8",
    boxShadow: validateButtonDisabled
      ? "none"
      : "0 12px 24px rgba(29, 78, 216, 0.28)",
    opacity: validateButtonDisabled ? 0.65 : 1,
    transition: "all 0.2s ease",
    ...(validateButtonStyle && typeof validateButtonStyle === "object"
      ? validateButtonStyle
      : {}),
  };

  const isSubmitBlocked = Boolean(
    submitDisabled || disableModalButtons || checkingAssociation,
  );
  const resolvedSubmitButtonStyle = isSubmitBlocked
    ? {
        opacity: 0.55,
        cursor: "not-allowed",
        background: "#94a3b8",
        borderColor: "#94a3b8",
        boxShadow: "none",
        color: "#ffffff",
      }
    : undefined;
  // alert (secLabel);

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="modal ap-modal">
        <h2 id={titleId}>Reactivate ATTUID</h2>
        <p>Review worker details and submit reactivation request.</p>

        <label>Worker Name</label>
        <input type="text" readOnly value={workerName || ""} />

        <label>DTVID</label>
        <input type="text" readOnly value={workerDtvId || ""} />

        <label>ATTUID</label>
        <input type="text" readOnly value={workerAttuid || ""} />

        <label
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <span>{secLabel}</span>
          {effectiveShowSecurityEditButton && (
            <button
              type="button"
              onClick={disableModalButtons ? undefined : onEnableSecurityEdit}
              style={{
                border: "1px solid #cbd5e1",
                background: "#fff",
                color: "#0f172a",
                padding: "2px 10px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 12,
                lineHeight: "20px",
              }}
              disabled={disableModalButtons}
            >
              Edit
            </button>
          )}
          {effectiveShowSecurityDisableEditButton && (
            <button
              type="button"
              onClick={disableModalButtons ? undefined : onDisableSecurityEdit}
              style={{
                border: "1px solid #cbd5e1",
                background: "#fff",
                color: "#0f172a",
                padding: "2px 10px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 12,
                lineHeight: "20px",
              }}
              disabled={disableModalButtons}
            >
              Disable
            </button>
          )}
        </label>

        <div
          style={{
            position: "relative",
            display: "inline-block",
            width: "100%",
          }}
        >
          <input
            type="text"
            value={securityRenderValue}
            readOnly={securityLocked}
            title={securityLocked ? `Click Edit to update ${secLabel}` : ""}
            autoComplete="off"
            inputMode={isUS ? "numeric" : "text"}
            style={{
              width: "100%",
              paddingRight: 42,
              boxSizing: "border-box",
              backgroundColor: securityLocked ? "#e5e7eb" : undefined,
              cursor: securityLocked ? "not-allowed" : undefined,
              opacity: securityLocked ? 0.9 : 1,
              color: securityLocked
                ? "#0f172a"
                : hideSecurityValue
                  ? "transparent"
                  : undefined,
              caretColor: securityLocked
                ? "transparent"
                : hideSecurityValue
                  ? "#0f172a"
                  : undefined,
            }}
            disabled={disableModalButtons}
            onPaste={(e) => {
              if (securityLocked || disableModalButtons) return;
              e.preventDefault();
              onSecurityChange(e.clipboardData.getData("text"));
            }}
            onChange={(e) => onSecurityChange(e.target.value)}
            placeholder={isUS ? "___-__-____" : "f-__-__-____"}
          />
          {!securityLocked && hideSecurityValue && securityInputValue && (
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: 12,
                right: 42,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#0f172a",
                pointerEvents: "none",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "clip",
              }}
            >
              {securityMaskDisplay}
            </div>
          )}
          <button
            type="button"
            aria-label={showSecurity ? `Hide ${secLabel}` : `Show ${secLabel}`}
            title={showSecurity ? "Hide" : "Show"}
            onMouseDown={(e) => e.preventDefault()}
            onClick={
              securityLocked || disableModalButtons
                ? undefined
                : onToggleShowSecurity
            }
            style={{
              position: "absolute",
              right: 6,
              top: "50%",
              transform: "translateY(-50%)",
              height: 26,
              width: 30,
              border: 0,
              background: "transparent",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#334155",
              padding: 0,
            }}
            disabled={disableModalButtons || securityLocked}
          >
            <EyeIcon />
          </button>
        </div>
        {errors?.securityId ? (
          <div className="meta" style={{ color: "#b91c1c", marginTop: 4 }}>
            {errors.securityId}
          </div>
        ) : null}

        <label>{confirmLabel}</label>
        <div
          style={{
            position: "relative",
            display: "inline-block",
            width: "100%",
          }}
        >
          <input
            type="text"
            value={confirmRenderValue}
            readOnly={securityLocked}
            title={securityLocked ? `Click Edit to update ${confirmLabel}` : ""}
            autoComplete="off"
            inputMode={isUS ? "numeric" : "text"}
            style={{
              width: "100%",
              paddingRight: 42,
              boxSizing: "border-box",
              backgroundColor: securityLocked ? "#e5e7eb" : undefined,
              cursor: securityLocked ? "not-allowed" : undefined,
              opacity: securityLocked ? 0.9 : 1,
              color: securityLocked
                ? "#0f172a"
                : hideConfirmSecurityValue
                  ? "transparent"
                  : undefined,
              caretColor: securityLocked
                ? "transparent"
                : hideConfirmSecurityValue
                  ? "#0f172a"
                  : undefined,
            }}
            disabled={disableModalButtons}
            onPaste={(e) => {
              if (securityLocked || disableModalButtons) return;
              e.preventDefault();
              onConfirmSecurityChange(e.clipboardData.getData("text"));
            }}
            onChange={(e) => onConfirmSecurityChange(e.target.value)}
            placeholder={isUS ? "___-__-____" : "f-__-__-____"}
          />
          {!securityLocked && hideConfirmSecurityValue && confirmInputValue && (
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: 12,
                right: 42,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#0f172a",
                pointerEvents: "none",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "clip",
              }}
            >
              {confirmMaskDisplay}
            </div>
          )}
          <button
            type="button"
            aria-label={
              showConfirmSecurity
                ? `Hide ${confirmLabel}`
                : `Show ${confirmLabel}`
            }
            title={showConfirmSecurity ? "Hide" : "Show"}
            onMouseDown={(e) => e.preventDefault()}
            onClick={
              securityLocked || disableModalButtons
                ? undefined
                : onToggleShowConfirmSecurity
            }
            style={{
              position: "absolute",
              right: 6,
              top: "50%",
              transform: "translateY(-50%)",
              height: 26,
              width: 30,
              border: 0,
              background: "transparent",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#334155",
              padding: 0,
            }}
            disabled={disableModalButtons || securityLocked}
          >
            <EyeIcon />
          </button>
        </div>
        {errors?.confirmSecurityId ? (
          <div className="meta" style={{ color: "#b91c1c", marginTop: 4 }}>
            {errors.confirmSecurityId}
          </div>
        ) : null}
        
        {/*For checking state*/}
        {!errors?.securityId &&
        !errors?.confirmSecurityId &&
        associationStatus === "checking" ? (
          <div className="meta" style={{ color: "#6b7280", marginTop: 4 }}>
            {associationMessage || `Checking ${secLabel}...¦`}
          </div>
        ) : null}

         {/*For found state */}
        {!errors?.securityId &&
        !errors?.confirmSecurityId &&
        associationStatus === "found" &&
        associationMessage ? (
          <div
            className="meta"
            style={{
              color: "#b45309",
              marginTop: 4,
            }}
          >
            {associationMessage}
          </div>
        ) : null}
        
        {!errors?.securityId &&
        !errors?.confirmSecurityId &&
        associationStatus === "none" &&
        associationMessage ? (
          <div className="meta" style={{ color: "#166534", marginTop: 4 }}>
            {associationMessage}
          </div>
        ) : null}

        {securityLocked ? (
          <div className="meta" style={{ color: "#6b7280", marginTop: 8 }}>
            Security details already exist for this worker. Validation is
            skipped.
          </div>
        ) : null}

        {missingCriticalFields?.length > 0 ? (
          <div className="meta" style={{ color: "#b91c1c", marginTop: 10 }}>
            Missing critical data: {missingCriticalFields.join(", ")}. Please
            edit the worker data using Edit.
          </div>
        ) : null}

        {validateStatus && validateStatus !== "idle" && validateMessage ? (
          <div
            className="meta"
            style={{ color: validateTone, marginTop: 10, fontWeight: 600 }}
          >
            {isInvalidRoleValidation ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div>{invalidRoleMessage}</div>
                {showInvalidRoleDetail ? <div>{validateMessage}</div> : null}
              </div>
            ) : (
              validateMessage
            )}
          </div>
        ) : null}
        {submitDisabled && submitMessage && validateStatus !== "failed" ? (
          <div className="meta" style={{ color: "#6b7280", marginTop: 6 }}>
            {submitMessage}
          </div>
        ) : null}

        <div className="modal-buttons" style={{ marginTop: 14 }}>
          <button onClick={onCancel} disabled={disableModalButtons}>
            Cancel
          </button>
          {onValidate ? (
            <button
              type="button"
              onClick={async () => {
                if (validateButtonDisabled) {
                  console.warn(
                    "[REACTIVATE MODAL] Validate blocked because button is disabled",
                  );
                  return;
                }

                setValidateClicked(true);
                try {
                  await onValidate();
                } finally {
                  setValidateClicked(false);
                }
              }}
              disabled={validateButtonDisabled}
              aria-disabled={validateButtonDisabled}
              title={
                loading
                  ? "Loading worker details..."
                  : checkingAssociation
                    ? `Checking ${secLabel}...`
                    : validateMessage || undefined
              }
              style={resolvedValidateButtonStyle}
            >
              {validateButtonLabel}
            </button>
          ) : null}
          <button
            className="primary"
            onClick={onSubmit}
            disabled={isSubmitBlocked}
            aria-disabled={isSubmitBlocked}
            style={resolvedSubmitButtonStyle}
            title={
              loading
                ? "Loading worker details..."
                : checkingAssociation
                  ? `Checking ${secLabel}...`
                  : submitDisabled && submitMessage
                    ? submitMessage
                    : undefined
            }
          >
            {submitButtonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
